import { Box, Button, GridItem, Heading, Stack, Text } from '@chakra-ui/react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import type { WhoWeAreDataFragment } from '@/server/data/generated/graphql'

import WebsiteSettingsQ from '@/queries/WebsiteSettingsQ'
import { useQuery } from '@/client/context/query'
import { getAboutUrl } from '@/client/services/url'

import Section from '@/components/Landing/Section'
import ShwGrid from '@/components/Layout/ShwGrid'
import { STATE_PARTNERS } from '@/components/Partners/constants'
import PublicImage from '@/components/PublicImage/PublicImage'
import ShwLink from '@/components/ShwLink'

import FromTheFounderOfKhanAcademy from './FromTheFounderOfKhanAcademy'
import PressPartners from './PressPartners'

export default function WhoWeAre({
  data,
  hideCTA = false,
}: {
  data?: WhoWeAreDataFragment
  hideCTA?: boolean
}): JSX.Element | null {
  const query = useQuery()

  let numLearners = 0
  let numSessions = 0

  if (data != null) {
    numLearners = data.statistics.countPublicLearners
    numSessions = data.statistics.countPublicSessions
  } else {
    numLearners = query(WebsiteSettingsQ.getPublicCountLearners()) ?? 0
    numSessions = query(WebsiteSettingsQ.getPublicCountSessions()) ?? 0
  }

  const numLearnersThousands = Math.floor(numLearners / 1000)
  const numSessionsThousands = Math.floor(numSessions / 1000)
  const stats = [
    { stat: `${numLearnersThousands}K+`, label: 'Learners' },
    { stat: `${numSessionsThousands}K+`, label: 'Sessions' },
    { stat: STATE_PARTNERS.length, label: 'Official U.S. State Partners' },
  ]

  return (
    <Section py={10} bg="bg.muted">
      <ShwGrid alignItems="center" rowGap={8}>
        <GridItem colSpan={[12, 12, 5, 6]}>
          <Box
            pr={['0px', '0px', '36px', '72px']}
            borderRight={['none', 'none', `2px solid ${chakraTokenToVar('border')}`]}
          >
            <FromTheFounderOfKhanAcademy />
            <Heading size="2xl" as="h2" mb="24px" mt="16px" color="fg.muted">
              We are a non-profit with the mission of connecting the world with free, peer-to-peer
              tutoring.
            </Heading>
            <Box>
              {!hideCTA ? (
                <ShwLink href={getAboutUrl()}>
                  <Button variant="outline" width={['100%', '100%', 'auto']}>
                    About Us
                  </Button>
                </ShwLink>
              ) : null}
            </Box>
          </Box>
        </GridItem>
        <GridItem colSpan={[12, 12, 7, 6]} width="100%">
          <Box pl={['0px', '0px', '0px', '36px']}>
            <Stack spacing="24px" direction="row" align="center" mb="48px">
              <PublicImage
                src="/people/sal-headshot.png"
                alt="Schoolhouse.world is founded by Sal Khan, the founder of Khan Academy."
                width="100px"
                borderRadius="50%"
              />
              <Box width={['auto', '250px', '300px', '350px']}>
                <Heading as="h3" size="lg" color="fg.muted" fontWeight={700} mb="4px">
                  Founded by Sal Khan
                </Heading>
                <Text fontSize="16px" fontWeight={600} color="fg.muted">
                  CEO of Khan Academy and Schoolhouse.world
                </Text>
              </Box>
            </Stack>
            <Stack direction="row" spacing="48px">
              {stats.map(({ stat, label }) => (
                <Box key={label}>
                  <Heading size="xl" color="fg.muted" fontSize="32px" mb="4px">
                    {stat}
                  </Heading>
                  <Text
                    fontWeight={600}
                    color="fg.muted"
                    fontSize="14px"
                    lineHeight="18px"
                    maxWidth={['100px', '100px', '100px', '100px', 'none']}
                  >
                    {label}
                  </Text>
                </Box>
              ))}
            </Stack>
            <Box mt="30px">
              <PressPartners />
            </Box>
          </Box>
        </GridItem>
      </ShwGrid>
    </Section>
  )
}
